import { useEffect, useState } from "react";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { BlueprintMinimalInfo } from "../../../api/Blueprints";
import { sortNamedListAlpha } from "../../../utils";
import { CompanyAssignCheckTypeButton } from "../../CompanyAssignCheckTypeButton";
import CompanyBlueprintRemove from "../../companyEdit/CompanyBlueprintRemove";
import { useCompanyUpdate } from "../../../api/CompaniesApi";
import { CompanyEx } from "../../../api/Companies";
import DropdownComboSimple, {
  DropdownItem,
} from "../../../shared-components/DropdownComboSimple";
import Button, { ButtonTypes } from "../../../shared-components/Button";

import CheckTypeDetail from "./CompanyBlueprintDetail";

type CompaniesBlueprintsListProps = {
  blueprintsList: BlueprintMinimalInfo[];
  companyUUID: string;
  company?: CompanyEx;
  isReadOnly: boolean;
  parentBlueprintsUUIDs?: string[];
  canUpdateDefaultBlueprint?: boolean;
  callbackOnChange?: () => void;
};

const CheckTypesList = ({
  blueprintsList,
  companyUUID,
  company,
  isReadOnly,
  parentBlueprintsUUIDs,
  canUpdateDefaultBlueprint,
  callbackOnChange,
}: CompaniesBlueprintsListProps) => {
  const { mutateAsync: companyUpdate, isLoading: isLoadingCompanyUpdate } =
    useCompanyUpdate();
  const [visibleBlueprints, setVisibleBlueprints] = useState<string[]>([]);
  const [localBlueprints, setLocalBlueprints] = useState<
    BlueprintMinimalInfo[]
  >([]);
  const [blueprintDefaultOptions, setBlueprintDefaultOptions] = useState<
    DropdownItem[]
  >([]);
  const [chosenDefaultBlueprint, setChosenDefaultBlueprint] =
    useState<string>("");

  useEffect(() => {
    setChosenDefaultBlueprint(company?.default_blueprint ?? "");
  }, [company]);

  useEffect(() => {
    const sortedBlueprints = sortNamedListAlpha(blueprintsList);
    setLocalBlueprints(sortedBlueprints);
  }, [blueprintsList]);

  useEffect(() => {
    const newBlueprints: DropdownItem[] = [];
    if (chosenDefaultBlueprint === "") {
      setChosenDefaultBlueprint(company?.default_blueprint ?? "null");
      return;
    }
    if (localBlueprints) {
      for (const match of localBlueprints.filter(
        (bp) => bp.blueprint_uuid === chosenDefaultBlueprint
      )) {
        newBlueprints.push({ id: match.blueprint_uuid, label: match.name });
      }
      if (
        !!chosenDefaultBlueprint &&
        chosenDefaultBlueprint !== "null" &&
        newBlueprints.length === 0
      ) {
        newBlueprints.push({
          id: chosenDefaultBlueprint,
          label: "<Not Found>",
        });
      }
      newBlueprints.push({ id: "null", label: "General" });
      for (const other of localBlueprints.filter(
        (bp) => bp.blueprint_uuid !== chosenDefaultBlueprint
      )) {
        newBlueprints.push({ id: other.blueprint_uuid, label: other.name });
      }
    } else {
      if (!!company?.default_blueprint) {
        newBlueprints.push({
          id: company.default_blueprint,
          label: "<Not Found>",
        });
      }
      newBlueprints.push({ id: "null", label: "General" });
    }
    setBlueprintDefaultOptions(newBlueprints);
  }, [company, chosenDefaultBlueprint, localBlueprints]);

  function handleBlueprintDefaultChanged(option: DropdownItem) {
    setChosenDefaultBlueprint(option.id);
  }

  async function saveBlueprintDefault() {
    const api_value =
      chosenDefaultBlueprint === "null" ? null : chosenDefaultBlueprint;
    await companyUpdate({
      companyUUID: companyUUID,
      updates: new Map([["default_blueprint", api_value]]),
    });
    if (callbackOnChange) {
      callbackOnChange();
    }
  }

  function handleAdd(savedBlueprints: BlueprintMinimalInfo[]) {
    localBlueprints.push(...savedBlueprints);
    setLocalBlueprints(sortNamedListAlpha(localBlueprints));
  }

  function handleRemove(blueprintUUID: string) {
    setLocalBlueprints(
      localBlueprints.filter((p) => p.blueprint_uuid !== blueprintUUID)
    );
  }

  const addVisibleBlueprint = (blueprintUUID: string) => {
    setVisibleBlueprints([...visibleBlueprints, blueprintUUID]);
  };

  const removeVisibleBlueprint = (blueprintUUID: string) => {
    setVisibleBlueprints(visibleBlueprints.filter((p) => p !== blueprintUUID));
  };

  function isVisibleBlueprint(blueprintUUID: string) {
    return visibleBlueprints.some((p) => p === blueprintUUID);
  }

  // TODO: validate the logic of the already selected blueprints for the subcompany
  const existingBlueprints =
    localBlueprints?.map((pivot) => pivot.blueprint_uuid || "") || [];

  return (
    <div>
      {canUpdateDefaultBlueprint && localBlueprints.length > 0 && (
        <div className="mb-6 p-4 bg-white rounded-md border border-gray-200">
          <div className="flex flex-col gap-3">
            <div className="text-sm font-medium text-gray-700">
              Default Check Type
            </div>
            <div className="flex gap-3 items-start">
              <div className="flex-1">
                <DropdownComboSimple
                  items={blueprintDefaultOptions}
                  initial={
                    blueprintDefaultOptions.find(
                      (item) => item.id === chosenDefaultBlueprint
                    ) || blueprintDefaultOptions[0]
                  }
                  onChange={handleBlueprintDefaultChanged}
                  name="defaultBlueprint"
                  disabled={isReadOnly}
                />
              </div>
              <Button
                text="Save"
                type={ButtonTypes.default}
                onClick={saveBlueprintDefault}
                disabled={
                  isReadOnly ||
                  isLoadingCompanyUpdate ||
                  chosenDefaultBlueprint ===
                    (company?.default_blueprint ?? "null")
                }
              />
            </div>
            <div className="text-xs text-gray-500">
              This configuration is for API integration only
            </div>
          </div>
        </div>
      )}
      {!localBlueprints ||
        (localBlueprints.length === 0 && (
          <div className="text-center rounded-md bg-white p-4 h-96 flex flex-col justify-center items-center border border-gray-200">
            <div className="font-medium">No Check Types Assigned</div>
            <div className="text-sm text-slate-400 py-4">
              There are no check types assigned to this company
            </div>
            {!isReadOnly && (
              <CompanyAssignCheckTypeButton
                existingBlueprints={[]}
                companyUUID={companyUUID}
                onSave={handleAdd}
                blueprintsFromParent={parentBlueprintsUUIDs}
                useButtonOutline={true}
              />
            )}
          </div>
        ))}
      {localBlueprints.length > 0 && (
        <>
          {localBlueprints.map((blueprint) => (
            <div
              className="rounded-md border-gray-200 border-2 p-3 mb-4 bg-white"
              key={blueprint.blueprint_uuid}
            >
              <div className="flex gap-4 truncate w-full items-center justify-between text-md">
                <span className="font-bold">{blueprint.name}</span>

                <div className="flex gap-2">
                  <div className="flex gap-2 ml-0 text-gray-500 justify-center items-center">
                    {!isReadOnly && (
                      <CompanyBlueprintRemove
                        onRemove={() => handleRemove(blueprint.blueprint_uuid)}
                        blueprintUUID={blueprint.blueprint_uuid}
                        companyUUID={companyUUID}
                      />
                    )}
                  </div>
                  <div className="text-gray-500 cursor-context-menu">
                    {isVisibleBlueprint(blueprint.blueprint_uuid) ? (
                      <ChevronUpIcon
                        className="w-5 h-5"
                        onClick={() =>
                          removeVisibleBlueprint(blueprint.blueprint_uuid)
                        }
                      />
                    ) : (
                      <ChevronDownIcon
                        className="w-5 h-5"
                        onClick={() =>
                          addVisibleBlueprint(blueprint.blueprint_uuid)
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
              {blueprint.blueprint_uuid && (
                <CheckTypeDetail
                  blueprintUUID={blueprint.blueprint_uuid ?? ""}
                  isVisible={isVisibleBlueprint(blueprint.blueprint_uuid)}
                />
              )}
            </div>
          ))}
          {!isReadOnly && (
            <CompanyAssignCheckTypeButton
              existingBlueprints={existingBlueprints}
              companyUUID={companyUUID}
              onSave={handleAdd}
              blueprintsFromParent={parentBlueprintsUUIDs || []}
              useButtonOutline={true}
            />
          )}
        </>
      )}
    </div>
  );
};

export default CheckTypesList;
